import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Clock, Flame } from 'lucide-react';
import { CCACandidateQuestion, CCAOptionCandidate } from '../../utils/types/cca_candidate_types';
import TankSVG from './TankSVG';
import ShellSVG from './ShellSVG';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TankQuestionScreenProps {
  token: string;
  question: CCACandidateQuestion;
  totalAssigned: number;
  answeredCount: number;
  onAnswer: (
    selectedOptionId: string | null,
    shortAnswerText: string | null,
    timeTaken: number,
  ) => void;
}

// ─── Ammo shell (progress indicator) ─────────────────────────────────────────

const AmmoShell: React.FC<{ spent: boolean }> = ({ spent }) => (
  <svg viewBox='0 0 8 28' width='8' height='28' className={spent ? 'tqs-ammo-spent' : 'tqs-ammo-full'}>
    <ellipse cx='4' cy='4' r='3' fill={spent ? '#555' : '#c8960c'} />
    <rect x='2.5' y='3' width='3' height='18' fill={spent ? '#666' : '#b08010'} />
    <rect x='1.5' y='18' width='5' height='7' rx='1' fill={spent ? '#555' : '#8a6208'} />
    <rect x='3' y='4' width='1.5' height='8' fill={spent ? '#777' : '#e8c030'} opacity='0.6' />
  </svg>
);

// ─── Timer hook ───────────────────────────────────────────────────────────────

function useTimer(limitSec: number, onExpire: () => void) {
  const [remaining, setRemaining] = useState(limitSec);
  const elapsedRef = useRef(0);
  const cbRef = useRef(onExpire);
  cbRef.current = onExpire;

  useEffect(() => {
    setRemaining(limitSec);
    elapsedRef.current = 0;
    let fired = false;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(id);
          if (!fired) { fired = true; setTimeout(() => cbRef.current(), 0); }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [limitSec]);

  return { remaining, getElapsed: () => elapsedRef.current };
}

// ─── TankQuestionScreen ───────────────────────────────────────────────────────

export const TankQuestionScreen: React.FC<TankQuestionScreenProps> = ({
  token,
  question,
  totalAssigned,
  answeredCount,
  onAnswer,
}) => {
  const [barrelAngle, setBarrelAngle] = useState(-0.3);
  const [firing, setFiring] = useState(false);
  const [shellPath, setShellPath] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [shortText, setShortText] = useState('');
  const [hitIdx, setHitIdx] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const tankRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([null, null, null, null]);
  const shortBoxRef = useRef<HTMLDivElement>(null);

  const isShortAnswer = question.questionType === 'short_answer';

  // Build CSS offset-path from barrel tip to a target element
  const buildPath = useCallback((targetEl: HTMLElement | null): { path: string; angle: number } => {
    const container = containerRef.current;
    const tankEl = tankRef.current;
    if (!container || !tankEl) return { path: '', angle: -0.3 };

    const cRect = container.getBoundingClientRect();
    const tRect = tankEl.getBoundingClientRect();
    const vpScale = (window as any).visualViewport?.scale ?? 1;

    let ex: number, ey: number;
    if (targetEl) {
      const eRect = targetEl.getBoundingClientRect();
      ex = (eRect.left - cRect.left + eRect.width * 0.5) / vpScale;
      ey = (eRect.top - cRect.top + eRect.height * 0.5) / vpScale;
    } else {
      ex = cRect.width * 0.62;
      ey = cRect.height * 0.42;
    }

    let sx: number, sy: number;
    if (tRect.width === 0 && tRect.height === 0) {
      sx = -20;
      sy = ey;
    } else {
      sx = (tRect.right - cRect.left - 12) / vpScale;
      sy = (tRect.top - cRect.top + tRect.height * 0.48) / vpScale;
    }

    const dx = ex - sx;
    const dy = ey - sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const arcH = -Math.min(0.08 * dist, 24);

    const cx = (sx + ex) * 0.5 + (dy / dist) * arcH;
    const cy = (sy + ey) * 0.5 + (-dx / dist) * arcH;

    const angle = Math.atan2(ey - sy, ex - sx);
    const path = `path('M ${sx.toFixed(1)} ${sy.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}')`;
    return { path, angle };
  }, []);

  const fireShell = useCallback((targetEl: HTMLElement | null) => {
    const { path, angle } = buildPath(targetEl);
    setBarrelAngle(angle);
    setTimeout(() => {
      setFiring(true);
      if (path) setShellPath(path);
      setTimeout(() => setFiring(false), 120);
      setTimeout(() => setShellPath(null), 650);
    }, 200);
  }, [buildPath]);

  const submit = useCallback((
    optionId: string | null,
    text: string | null,
    elapsed: number,
    optionIdx: number | null,
    targetEl: HTMLElement | null,
  ) => {
    if (locked) return;
    setLocked(true);
    if (optionIdx !== null) setHitIdx(optionIdx);
    fireShell(targetEl);
    setTimeout(() => {
      onAnswer(optionId, text, elapsed);
    }, 1050);
  }, [locked, fireShell, onAnswer]);

  const { remaining, getElapsed } = useTimer(
    question.timeLimitSeconds,
    useCallback(() => {
      if (!locked) submit(null, isShortAnswer ? shortText || null : null, question.timeLimitSeconds, null, null);
    }, [locked, isShortAnswer, shortText, question.timeLimitSeconds, submit]),
  );

  const handleOptionClick = useCallback((optId: string, idx: number) => {
    if (locked) return;
    setSelectedOption(optId);
    submit(optId, null, Math.min(getElapsed(), question.timeLimitSeconds), idx, optionRefs.current[idx]);
  }, [locked, submit, getElapsed, question.timeLimitSeconds]);

  const handleShortSubmit = useCallback(() => {
    if (locked || !shortText.trim()) return;
    submit(null, shortText, Math.min(getElapsed(), question.timeLimitSeconds), null, shortBoxRef.current);
  }, [locked, shortText, submit, getElapsed, question.timeLimitSeconds]);

  const pct = (remaining / question.timeLimitSeconds) * 100;
  const tColor = pct > 50 ? 'green' : pct > 25 ? 'yellow' : 'red';
  const totalDots = Math.min(totalAssigned, 30);

  return (
    <Box className='tqs-stage' ref={containerRef}>
      {/* Flying shell overlay */}
      {shellPath && (
        <Box
          className='tqs-fly-shell'
          sx={{ offsetPath: shellPath } as any}
        >
          <ShellSVG />
        </Box>
      )}

      {/* Timer bar */}
      <Box className='cca-timer-wrap'>
        <Box className={`cca-timer-fill cca-timer-fill--${tColor}`} sx={{ width: `${pct}%` }} />
      </Box>

      {/* Stage body */}
      <Box className='tqs-body'>
        {/* Left: Tank + ammo rack */}
        <Box className='tqs-left'>
          <Box className='tqs-ammo-rack'>
            <Box className='tqs-ammo-label'>Ammo</Box>
            <Box className='tqs-ammo-shells'>
              {Array.from({ length: Math.min(totalAssigned, 14) }).map((_, i) => (
                <AmmoShell key={i} spent={i < answeredCount} />
              ))}
            </Box>
          </Box>
          <Box ref={tankRef} className='tqs-tank'>
            <TankSVG barrelAngle={barrelAngle} firing={firing} />
          </Box>
        </Box>

        {/* Right: Question + Options */}
        <Box className='tqs-right'>
          {/* Header */}
          <Box className='cca-q-header'>
            <span className='cca-q-number'>
              Target {question.questionNumber} of {question.totalQuestions}
            </span>
            <Box
              component='span'
              className={`cca-q-timer-text cca-q-timer-text--${tColor}`}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Clock size={14} /> {remaining}s
            </Box>
          </Box>

          {/* Question card */}
          <Box className='tqs-question-card'>
            <Box className='cca-q-meta'>
              <span className='cca-q-meta-tag'>{question.subdomain.replace(/_/g, ' ')}</span>
              <span className='cca-q-meta-tag'>Level {question.level}</span>
              <span className='cca-q-meta-tag'>{isShortAnswer ? 'Short Answer' : 'Multiple Choice'}</span>
            </Box>
            {question.question.text && (
              <p className='cca-q-text'>{question.question.text}</p>
            )}
            {question.question.imageUrl && (
              <Box component='img' src={question.question.imageUrl} alt='Question' className='cca-q-image' />
            )}
          </Box>

          {/* MCQ options */}
          {!isShortAnswer && (
            <Box className='tqs-options-grid'>
              {question.options.map((opt: CCAOptionCandidate, i: number) => (
                <button
                  key={opt.id}
                  ref={el => { optionRefs.current[i] = el; }}
                  className={[
                    'tqs-option',
                    selectedOption === opt.id ? 'tqs-option--selected' : '',
                    hitIdx === i ? 'tqs-option--hit' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleOptionClick(opt.id, i)}
                  disabled={locked}
                >
                  <span className='tqs-opt-marker'>{String.fromCharCode(65 + i)}</span>
                  <span className='tqs-opt-body'>
                    {opt.text && <span className='tqs-opt-text'>{opt.text}</span>}
                    {opt.imageUrl && (
                      <Box component='img' src={opt.imageUrl} alt={`Option ${String.fromCharCode(65 + i)}`} className='tqs-opt-img' />
                    )}
                  </span>
                </button>
              ))}
            </Box>
          )}

          {/* Short answer */}
          {isShortAnswer && (
            <Box className='tqs-short' ref={shortBoxRef}>
              <textarea
                className='tqs-short-input'
                placeholder='Type your answer here…'
                value={shortText}
                onChange={e => !locked && setShortText(e.target.value)}
                disabled={locked}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleShortSubmit(); }}
              />
              <button
                className='tqs-fire-btn'
                onClick={handleShortSubmit}
                disabled={locked || !shortText.trim()}
              >
                <Flame size={15} style={{ display: 'inline', marginRight: 6 }} />
                Fire!
              </button>
            </Box>
          )}

          {/* Progress dots */}
          <Box className='cca-progress'>
            {Array.from({ length: totalDots }).map((_, i) => {
              const cls =
                i < answeredCount ? 'cca-dot--done'
                : i === answeredCount ? 'cca-dot--current'
                : 'cca-dot--empty';
              return <Box component='span' key={i} className={`cca-dot ${cls}`} />;
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TankQuestionScreen;
